import PlayCircleFilledWhiteOutlined from '@mui/icons-material/PlayCircleFilledWhiteOutlined';
import { Paper, Theme, Tooltip } from '@mui/material';
import { orange } from '@mui/material/colors';
import IconButton from '@mui/material/IconButton';
import makeStyles from '@mui/styles/makeStyles';
import { GridValueFormatterParams } from '@mui/x-data-grid';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useMutation } from 'react-query';
import { Link } from 'react-router-dom';
import { useRecoilStateLoadable } from 'recoil';

import { useBusinessPermissions } from '../../common/helperFunctions/useBusinessPermissions';
import { loadableEnum } from '../../common/useMakePHPRequest';
import { trackAddLeaderboardSlide } from '../../common/userTracking';
import { IBusinessModel, IUserAtom, user } from '../../recoil/atoms/user';
import { methodNameEnum } from '../../recoil/selectors/makePHPRequest';
import { getApiMutation } from '../../services/rest/getApiMutation';
import {
  endpointEnum,
  makeApiRequestCached,
} from '../../services/rest/makeAPIRequest';
import { useSnackbarWithStyles } from '../components/notifications/SnackbarProviderWithStyles';
import { ActionColumn } from '../modules/Manage/pages/queues-table/ActionColumns';
import dataTableScss from '../modules/Manage/pages/queues-table/data-table.module.css';
import {
  DataTable,
  IColDef,
} from '../modules/Manage/pages/queues-table/DataTable';
import {
  EnhancedTableToolbar,
  ToolbarAddDeleteButton,
} from '../modules/Manage/pages/queues-table/EnhancedTableToolbar';
import { QueuesSkeleton } from '../modules/Skeleton/QueuesSkeleton';

const useStyles = makeStyles((theme: Theme) => ({
  fullWidth: {
    width: '100% !important',
  },
  buttonProgress: {
    // color: green[500],
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -22,
    marginLeft: -22,
  },
  actionColumn: {
    display: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  deleteIconButton: {
    color: theme.custom.button.delete.main,
  },
  numbersList: {
    padding: '0',
    paddingBottom: '1px',
    listStyleType: 'none',
    '&:last-child': {
      paddingBottom: 0,
    },
  },
  actionButton: {
    marginLeft: 'auto',
    display: 'flex',
    flexWrap: 'nowrap',
  },
  peopleIconButton: {
    color: orange[500],
  },
}));

export function ManageLeaderboards() {
  const classes = useStyles();
  const [userAtomLoadable, setUserAtom] = useRecoilStateLoadable(user);
  const userAtomContents = userAtomLoadable.contents as IUserAtom;
  // const [selected, setSelected] = useState<GridRowData[]>([]);
  /**
   * Callback used by column button to delete queue
   * @param id Queue to be deleted
   */
  const { enqueueSnackbarWithStyles } = useSnackbarWithStyles();
  const [businessDataCached] = makeApiRequestCached<IBusinessModel | undefined>(
    {
      endpoint: endpointEnum.GET_BUSINESS_DATA,
      payloadJsonString: JSON.stringify({
        call: methodNameEnum.GET_BUSINESS_DATA,
      }),
    },
    (loadable, cached) =>
      loadable.contents?.response?.data?.result?.[0] ?? cached.current
  );
  const [
    _,
    leaderboardsLoadable,
    setRefetchToggleForLeaderboards,
  ] = makeApiRequestCached(
    {
      method: 'post',
      // stage: 'itn',
      domain: process.env.VITE_APP_CDK_DOMAIN,
      endpoint: endpointEnum.GET_LEADERBOARDS,
      payloadJsonString: JSON.stringify({
        apikey: userAtomContents?.apikey,
        businessid: userAtomContents?.businessid,
        businessId: userAtomContents?.businessid,
      }),
    },
    (loadable, cached) =>
      loadable.contents?.response?.data?.result ?? cached.current
  );
  const leaderboardsCached = useRef({} as any);
  leaderboardsCached.current = useMemo(
    () =>
      leaderboardsLoadable.state === 'hasValue'
        ? leaderboardsLoadable.contents?.response?.data?.data
            ?.map((a: any) => ({
              ...a,
              id: a.leaderboardId,
            }))
            ?.sort((a: any, b: any) =>
              a?.name?.toLowerCase()?.localeCompare(b?.name?.toLowerCase())
            )
        : leaderboardsCached.current,
    [leaderboardsLoadable, userAtomContents]
  );
  const [sortModel, setGridSortModel] = useState([
    { field: 'name', sort: 'asc' },
  ]);

  const [deleteStatus, setDeleteStatus] = useState(loadableEnum.EMPTY);
  const [deleteLeaderboardId, setDeleteLeaderboardId] = useState<
    string | number | undefined
  >(0);

  const mutation = useMutation(getApiMutation(userAtomContents, setUserAtom));

  const handleDeleteLeaderboard = async (leaderboardId: string | number) => {
    let errorMessage = '';

    setDeleteStatus(loadableEnum.LOADING);

    try {
      const response = await mutation.mutateAsync({
        domain: process.env.VITE_APP_CDK_DOMAIN,
        endpoint: endpointEnum.DELETE_LEADERBOARD,
        payload: {
          apikey: userAtomContents.apikey,
          businessid: userAtomContents.businessid,
          leaderboardId,
        },
      });

      if (response?.data?.success) {
        setRefetchToggleForLeaderboards({});

        setDeleteStatus(loadableEnum.HAS_VALUE);
      }
    } catch (exception) {
      errorMessage = 'Failed to delete leaderboard';
      setDeleteStatus(loadableEnum.HAS_ERROR);
    }

    enqueueSnackbarWithStyles({
      message: errorMessage || 'Successfully deleted leaderboard',
      alertProps: { severity: errorMessage ? 'error' : 'success' },
    });
  };
  const readOnly = useBusinessPermissions().accountReadOnly;
  const columns: IColDef[] = [
    {
      field: 'name',
      headerName: 'Display Name',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
      enableSorting: true,
    },
    {
      field: 'id',
      headerName: 'Actions',
      disableExport: true,
      disableSearch: true, // do not search field content
      width: 150,
      align: 'center',
      headerAlign: 'center',
      enableSorting: false,
      renderCell: ActionColumn({
        deleteItemId: deleteLeaderboardId,
        setDeleteItemId: setDeleteLeaderboardId,
        isLoading: deleteStatus === loadableEnum.LOADING,
        handleDeletion: handleDeleteLeaderboard,
        editLink: '/manage/leaderboards/:id',
        renderAction: (params: GridValueFormatterParams) => (
          <Tooltip title="Launch">
            <IconButton
              name="members"
              onClick={() => {
                window.location.href = `/leaderboard/${params.row.leaderboardId}`;
              }}
              size="large"
            >
              <PlayCircleFilledWhiteOutlined
                className={classes.peopleIconButton}
              />
            </IconButton>
          </Tooltip>
        ),
      }),
    },
  ];
  const ToolbarComponent = useCallback(
    (props) => (
      <EnhancedTableToolbar
        {...props}
        dataType="RingGroups"
        // numSelected={selected.length}
        csvData={false}
        hideSearch={true}
        leftComponent={
          <>
            <p style={{ fontSize: '22px', marginBottom: 0 }}>
              Leaderboard Displays for{' '}
              <span style={{ fontWeight: 'bold' }}>
                {' '}
                {businessDataCached?.current?.name}
              </span>
            </p>
          </>
        }
        rightComponent={
          <>
            <Link to="/manage/leaderboards/new?add=agent">
              <ToolbarAddDeleteButton
                addButtonText="New Display"
                onClickForAdd={() => {
                  trackAddLeaderboardSlide();
                }}
                isLoading={props.isLoading}
              />
            </Link>
            {/*{' '}
            <Link to="/manage/leaderboards/new?add=agent">
              <ToolbarAddDeleteButton
                addButtonText="Agents slide"
                onClickForAdd={() => {}}
                isLoading={props.isLoading}
              />
            </Link>
            <Link to="/manage/leaderboards/new?add=queue">
              <ToolbarAddDeleteButton
                addButtonText="Queues slide"
                onClickForAdd={() => {
                  // history.push('/manage/numbers/routing/add');
                }}
                isLoading={props.isLoading}
              />
            </Link>{' '}
            */}
          </>
        }
      />
    ),
    [
      // selected.length,
      readOnly,
    ]
  );
  return (
    <Paper square={true} elevation={0}>
      {leaderboardsLoadable.state === 'loading' &&
      !leaderboardsCached.current?.length ? (
        <QueuesSkeleton />
      ) : (
        <DataTable
          enableSelectAll={true}
          // setSelected={setSelected}
          loading={leaderboardsLoadable?.state === 'loading'}
          // rows={ringGroupsCached.current}
          rows={leaderboardsCached?.current ?? []}
          columns={columns}
          ToolbarComponent={ToolbarComponent}
          sortModel={sortModel as any}
          setGridSortModel={setGridSortModel}
        />
      )}
    </Paper>
  );
}
